import React, { ReactNode } from 'react';
import { 
  Typography, 
  Box, 
  Card, 
  CardContent,
  useTheme
} from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register the plugin
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface ChartCardProps {
  positivePct: number;
  neutralPct: number;
  negativePct: number;
  posCount?: number;
  neuCount?: number;
  negCount?: number;
  titleComponent?: ReactNode;
  animateChart?: boolean;
  isPdfMode?: boolean;
  onChartRendered?: (chartName: string, imageDataUrl: string) => void;
  chartName?: string;
}

const ChartCard: React.FC<ChartCardProps> = ({ 
  positivePct, 
  neutralPct, 
  negativePct,
  posCount,
  neuCount,
  negCount,
  titleComponent,
  animateChart = true,
  isPdfMode = false,
  onChartRendered,
  chartName = 'sentimentChart'
}) => {
  const chartRef = React.useRef<ChartJS<'doughnut'>>(null);
  const legendRef = React.useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const mode = theme.palette.mode;

  // Ensure Chart.js default text colors match theme for legends and scales
  React.useEffect(() => {
    // These defaults affect the canvas-drawn legend text color
    (ChartJS.defaults as any).color = theme.palette.text.primary;
    if ((ChartJS.defaults as any).plugins?.legend?.labels) {
      (ChartJS.defaults as any).plugins.legend.labels.color = theme.palette.text.primary;
    }
  }, [theme.palette.text.primary, mode]);

  // Effect to capture chart for PDF when ready
  React.useEffect(() => {
    if (onChartRendered && chartRef.current && !isPdfMode) {
      // Small delay to ensure chart is fully rendered
      const timer = setTimeout(() => {
        if (chartRef.current) {
          const imageDataUrl = chartRef.current.toBase64Image();
          onChartRendered(chartName, imageDataUrl);
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [onChartRendered, chartName, isPdfMode, positivePct, neutralPct, negativePct]);

  // Responsive breakpoint detection
  // Chart data
  const data = {
    labels: ['Positive', 'Neutral', 'Negative'],
    datasets: [
      {
        data: (animateChart && !isPdfMode) ? [positivePct, neutralPct, negativePct] : [positivePct, neutralPct, negativePct],
        backgroundColor: [
          '#06D6A0',  // Modern green for positive
          '#AAAAAA',  // Modern grey for neutral
          '#FF6B6B',  // Modern red for negative
        ],
        borderColor: [
          '#05C391',
          '#999999',
          '#FF5252',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Get counts array for tooltips and legend
  const counts = [posCount, neuCount, negCount];
  const hasCountData = posCount !== undefined && neuCount !== undefined && negCount !== undefined;

  // Create an HTML legend plugin so we can fully control text color via CSS
  const htmlLegendPlugin = React.useMemo(() => ({
    id: 'htmlLegend',
    afterUpdate: (chart: any) => {
      const container = legendRef.current;
      if (!container) return;
      while (container.firstChild) {
        container.firstChild.remove();
      }
      const ul = document.createElement('ul');
      ul.style.display = 'flex';
      ul.style.flexWrap = 'wrap';
      ul.style.listStyle = 'none';
      ul.style.margin = '8px 0 0 0';
      ul.style.padding = '0 8px';
      ul.style.width = '100%';
      ul.style.justifyContent = 'flex-start';
      ul.style.gap = '6px 16px';

      const items = chart.options.plugins.legend.labels.generateLabels(chart);
      items.forEach((item: any) => {
        const li = document.createElement('li');
        li.style.display = 'flex';
        li.style.alignItems = 'center';
        li.style.marginRight = '16px';
        li.style.marginBottom = '6px';
        // Allow wrapping when space is tight
        li.style.whiteSpace = 'normal';
        li.style.cursor = 'default';

        const box = document.createElement('span');
        box.style.display = 'inline-block';
        box.style.width = '12px';
        box.style.height = '12px';
        box.style.borderRadius = '3px';
        box.style.marginRight = '8px';
        box.style.background = item.fillStyle as string;
        box.style.border = `1px solid ${item.strokeStyle}`;

        const text = document.createElement('span');
        text.style.color = theme.palette.text.primary;
        text.style.fontSize = '0.875rem';
        text.style.lineHeight = '1.2';
        text.appendChild(document.createTextNode(item.text));

        li.appendChild(box);
        li.appendChild(text);
        ul.appendChild(li);
      });
      container.appendChild(ul);
    },
  }), [theme.palette.text.primary]);

  // Chart options with type casting to avoid TypeScript errors
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Set default text color for chart elements
    color: theme.palette.text.primary,
    layout: { padding: 0 },
    animation: {
      duration: (animateChart && !isPdfMode) ? 1000 : 0,
      onComplete: () => {
        if (isPdfMode && chartRef.current && onChartRendered) {
          const imageDataUrl = chartRef.current.toBase64Image();
          onChartRendered(chartName, imageDataUrl);
        }
      }
    },
    plugins: {
      legend: {
        // Hide canvas legend; we render our own HTML legend below the chart
        display: false,
        position: 'bottom' as const,
        labels: {
          color: theme.palette.text.primary,
          generateLabels: function(chart: any) {
            const datasets = chart.data.datasets;
            return chart.data.labels.map(function(label: any, i: number) {
              const meta = chart.getDatasetMeta(0);
              const style = meta.controller.getStyle(i);
              
              let countText = '';
              if (hasCountData && counts[i] !== undefined) {
                countText = ` (${counts[i]})`;
              }
              
              return {
                text: `${label}: ${datasets[0].data[i]}%${countText}`,
                fillStyle: style.backgroundColor,
                strokeStyle: style.borderColor,
                lineWidth: style.borderWidth,
                hidden: false,
                index: i
              };
            });
          }
        }
      },
      tooltip: {
        enabled: (animateChart && !isPdfMode),
        backgroundColor: mode === 'dark' ? 'rgba(15,23,32,0.9)' : '#ffffff',
        titleColor: mode === 'dark' ? '#e6edf3' : '#111827',
        bodyColor: mode === 'dark' ? '#e6edf3' : '#111827',
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.raw || 0;
            const index = context.dataIndex;
            
            if (hasCountData && counts[index] !== undefined) {
              return `${label}: ${value}% (${counts[index]} replies)`;
            }
            
            return `${label}: ${value}%`;
          }
        }
      },
      // Using any type to work around type checking for the plugin
      // @ts-ignore
      datalabels: {
        display: (animateChart && !isPdfMode),
        color: mode === 'dark' ? '#FFFFFF' : '#1f2937',
        font: {
          weight: 'bold',
          size: 16
        },
        formatter: (value: number) => {
          return value >= 5 ? `${value}%` : '';
        },
        align: 'center',
        anchor: 'center'
      }
    }
  } as any; // Type assertion to bypass strict type checking

  return (
    <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        p: { xs: 1.5, md: 3 }, // More aggressive padding reduction on mobile
        '&:last-child': { pb: { xs: 1.5, md: 3 } } // Override MUI's last-child padding
      }}>
        {titleComponent || (
          <Typography variant="h5" gutterBottom>
            Sentiment Analysis
          </Typography>
        )}
        <Box sx={{ 
          flexGrow: 0, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          position: 'relative',
          height: { xs: 240, md: isPdfMode ? 220 : 260 },
          maxHeight: 320,
          width: '100%',
          overflow: 'visible'
        }}>
          <Box sx={{ width: '100%', height: '100%' }}>
            <Doughnut 
              ref={chartRef}
              data={data} 
              options={options} 
              // Force remount on theme mode change so legend/text colors update
              key={`${mode}-${(animateChart && !isPdfMode) ? 'animated' : 'static'}`}
              plugins={[htmlLegendPlugin]}
            />
          </Box>
        </Box>
        {/* HTML Legend container (outside chart area so canvas gets full height) */}
        <Box ref={legendRef} sx={{ mt: 2, px: { xs: 1, md: 2 }, width: '100%', overflow: 'visible' }} />
      </CardContent>
    </Card>
  );
};

export default ChartCard; 
